import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { 
  GET_ALL_LEVELS, 
  CREATE_LEVEL, 
  UPDATE_LEVEL, 
  DELETE_LEVEL 
} from '../graphql/levelOperations';
import { 
  TrashIcon, 
  PencilIcon, 
  PlusIcon
} from '@heroicons/react/24/solid';
import { handleGraphQLError, formatDate } from '../utils/errorHandler';
import { toast } from 'react-toastify';
import { useTheme } from '../contexts/ThemeContext';
import ManagementModal from '../components/ManagementModal';

const LevelsManagement = () => {
  const { isDarkMode } = useTheme();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [levelData, setLevelData] = useState({
    name: ''
  });
  const [editingLevel, setEditingLevel] = useState(null);

  // Fetch all levels
  const { loading, error, data, refetch } = useQuery(GET_ALL_LEVELS, {
    onError: (error) => handleGraphQLError(error, 'Failed to load levels')
  });

  // Mutations
  const [createLevel] = useMutation(CREATE_LEVEL, {
    onCompleted: () => {
      resetForm();
      refetch();
      toast.success('Level created successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to create level')
  });

  const [updateLevel] = useMutation(UPDATE_LEVEL, {
    onCompleted: () => {
      resetForm();
      refetch();
      toast.success('Level updated successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to update level')
  });

  const [deleteLevel] = useMutation(DELETE_LEVEL, {
    onCompleted: () => {
      refetch();
      toast.success('Level deleted successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to delete level')
  });

  const resetForm = () => {
    setLevelData({
      name: ''
    });
    setEditingLevel(null);
    setIsModalOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!levelData.name.trim()) {
      toast.error('Level name is required');
      return;
    }

    const input = {
      name: levelData.name.trim()
    };

    if (editingLevel) {
      updateLevel({
        variables: {
          updateLevelId: editingLevel.id,
          input
        }
      });
    } else {
      createLevel({
        variables: { input }
      });
    }
  };

  const handleEdit = (level) => {
    setEditingLevel(level);
    setLevelData({
      name: level.name
    });
    setIsModalOpen(true);
  };

  const handleDelete = (levelId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this level?');
    if (confirmDelete) {
      deleteLevel({
        variables: { deleteLevelId: levelId }
      });
    }
  };

  // Loading and error states
  if (loading) return (
    <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
      Loading levels...
    </div>
  );

  if (error) return (
    <div className={`text-center py-8 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`}>
      Error loading levels
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Add Level Button */}
      <div className="flex justify-end mb-4">
        <button 
          onClick={() => {
            setEditingLevel(null);
            setIsModalOpen(true);
          }}
          className={`
            flex items-center space-x-2 px-4 py-2 rounded-lg
            ${isDarkMode 
              ? 'bg-blue-600 text-white hover:bg-blue-500' 
              : 'bg-blue-500 text-white hover:bg-blue-600'
            }
            transition
          `}
        >
          <PlusIcon className="h-5 w-5" />
          <span>Add Level</span>
        </button>
      </div>

      {/* Levels Management Modal */}
      <ManagementModal
        isOpen={isModalOpen}
        onClose={() => {
          resetForm();
          setIsModalOpen(false);
        }}
        onSubmit={handleSubmit}
        title={editingLevel ? 'Edit Level' : 'Create New Level'}
      >
        {/* Level Name Input */}
        <div>
          <label 
            htmlFor="name" 
            className={`block mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
          >
            Level Name
          </label>
          <input
            type="text"
            id="name"
            value={levelData.name}
            onChange={(e) => setLevelData({...levelData, name: e.target.value})}
            className={`
              w-full px-3 py-2 rounded-lg
              ${isDarkMode 
                ? 'bg-gray-700 text-gray-200 border-gray-600 focus:border-blue-500' 
                : 'bg-white text-gray-800 border-gray-300 focus:border-blue-500'
              }
              border focus:outline-none focus:ring-1 focus:ring-blue-500
              transition
            `}
            placeholder="Enter level name"
            required
          />
        </div>
      </ManagementModal>

      {/* Levels List */}
      <div className={`
        ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
        shadow-lg rounded-xl border 
        transition-colors duration-300
      `}>
        {/* Card Header */}
        <div className={`
          px-6 py-4 
          ${isDarkMode ? 'border-b-gray-700' : 'border-b-gray-200'}
          border-b 
          flex justify-between items-center
        `}>
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            Levels List
          </h2>
          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Total: {data?.getAllLevels?.length || 0} levels
          </div>
        </div>

        {/* Levels Table */}
        <div className="overflow-x-auto">
          {data.getAllLevels.length === 0 ? (
            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No levels found
            </div>
          ) : (
            <table className="w-full text-sm text-left">
              <thead className={`
                ${isDarkMode 
                  ? 'bg-gray-700 text-gray-300' 
                  : 'bg-gray-50 text-gray-600'
                } 
                border-b border-gray-200 dark:border-gray-600
              `}>
                <tr>
                  <th className="px-6 py-3 text-left">Name</th>
                  <th className="px-6 py-3 text-left">Created At</th>
                  <th className="px-6 py-3 text-left">Updated At</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.getAllLevels.map((level) => (
                  <tr 
                    key={level.id} 
                    className={`
                      border-b 
                      ${isDarkMode 
                        ? 'border-gray-700 hover:bg-gray-700' 
                        : 'border-gray-200 hover:bg-gray-50'
                      }
                      transition duration-150
                    `}
                  >
                    <td className={`px-6 py-4 font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {level.name}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {formatDate(level.createdAt)}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {formatDate(level.updatedAt)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end space-x-2">
                        <button 
                          onClick={() => handleEdit(level)}
                          className={`
                            p-2 rounded-full 
                            ${isDarkMode 
                              ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-700' 
                              : 'text-blue-500 hover:text-blue-700 hover:bg-blue-100'
                            }
                            transition
                          `}
                          title="Edit"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button 
                          onClick={() => handleDelete(level.id)}
                          className={`
                            p-2 rounded-full 
                            ${isDarkMode 
                              ? 'text-red-400 hover:text-red-300 hover:bg-red-700' 
                              : 'text-red-500 hover:text-red-700 hover:bg-red-100'
                            }
                            transition
                          `}
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default LevelsManagement;
